import React from 'react'
import { MappedWallboxForWidgets, WallboxWidgetsData } from 'src/Libs/dashboard-widgets/hooks/use-data-for-wallbox-widgets'
import { VehicleWidgetData, WidgetConfiguration } from '../../types'
import WallboxWidgetContainer from './WallboxWidgetContainer'
import { useVehiclesConnectingToWallboxSocket, useVehiclesInformationSocket } from 'src/Libs/e-mobility/vehicle-integration/hooks'
import VehicleConnectingToWallboxSkeleton from '../vehicle-integration-widget/VehicleConnectingToWallboxSkeleton'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

type Props = WallboxWidgetsData

/*
 * A component that renders all wallbox widgets on the dashboard.
 */
export const useWallboxWidgets = ({
  mappedWallboxesForWidget,
  installationId,
  storageId,
  isWallboxWidgetDataLoading,
  hide,
}: Props & WidgetConfiguration): React.ReactElement[] => {
  const vehiclesInformationSocket = useVehiclesInformationSocket(installationId ? Number(installationId) : undefined)
  const vehiclesIdentificationSocket = useVehiclesConnectingToWallboxSocket(installationId ? Number(installationId) : undefined)
  const { t } = useCustomTranslation()

  if (hide || !mappedWallboxesForWidget?.length) return []

  /* map wallboxes with vehicles that are online and connected to it */
  const wallboxesWithVehicle: (MappedWallboxForWidgets & { vehicleData?: VehicleWidgetData })[] = mappedWallboxesForWidget.map(wallbox => {
    const chargingStatusOfWallbox = wallbox.chargingData?.chargingStatus

    if (wallbox.wallboxId === undefined) return wallbox

    if (!chargingStatusOfWallbox) return wallbox

    /* find a vehicle that is online and connected to the wallbox */
    const onlineVehicles = vehiclesInformationSocket.data?.filter(
      vehicle => vehicle.chargingState !== 'UNPLUGGED' && vehicle.wallboxId === String(wallbox.wallboxId)
    )

    if (!onlineVehicles || onlineVehicles.length === 0) return wallbox

    const { stateOfCharge, estimatedRange, chargingState, chargeLimitMax, vehicleName, vehicleId, customName, targetStateOfCharge, activeSchedule } =
      onlineVehicles[0]
    const onlineVehicleConnectedToWallbox: VehicleWidgetData = {
      vehicleId,
      vehicleName,
      stateOfCharge,
      estimatedRange,
      chargingState,
      chargeLimitMax,
      customName,
      targetStateOfCharge,
      activeSchedule,
      link: `/vehicle-integration/${installationId}/vehicle-integration?vehicleId=${vehicleId}`,
    }

    return { ...wallbox, vehicleData: onlineVehicleConnectedToWallbox }
  })

  return wallboxesWithVehicle
    .toSorted((wallboxA, wallboxB) => wallboxA.wallboxId?.localeCompare(wallboxB.wallboxId || ''))
    .map(wallbox => {
      /* check if a vehicle is in the process of connecting to the wallbox */
      const isVehicleConnectingToThisWallbox =
        vehiclesIdentificationSocket.data?.inProgress && vehiclesIdentificationSocket.data?.wallboxIds.includes(wallbox.wallboxId)

      if (isVehicleConnectingToThisWallbox)
        return (
          <VehicleConnectingToWallboxSkeleton
            id={wallbox.id.toString()}
            key={`wallbox-${wallbox.id}`}
            title={wallbox.deviceName ?? ''}
            subtitle={t('dashboard.vehicle-integration.widgets.connecting-to-wallbox.subtitle')}
            wallboxIndex={wallbox.number}
            installationId={installationId}
            wallboxStorageId={wallbox.storageSerialNumber}
            storageId={storageId}
          />
        )

      return (
        <WallboxWidgetContainer
          key={`wallbox-${wallbox.id}`}
          title={wallbox.deviceName}
          id={`wallbox-card-${wallbox.id}`}
          wallboxData={wallbox}
          installationId={installationId}
          storageId={storageId}
          isLoading={isWallboxWidgetDataLoading}
          vehicle={wallbox.vehicleData}
        />
      )
    })
}
